import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { PieChart, Info, Maximize2, X, Download, ChevronDown } from 'lucide-react';

const InteractivePieChart = ({
  data,
  title,
  componentsByCategory,
  buildingInfo,
  rightPanelOverride,
  hideDefaultLegends = false,
  categoryDataOverride = null,
  disableAdvancedControls = false,
  forcePercentagesAboveBars = false,
  hideCategoryLegend = false,
  hideComponentLegend = false,
  contentClassName = '',
  mobileChartScale = '',
  onPopupOpenChange,
  onPopupCloseRequest,
  closePopupSignal,
}) => {
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [componentSortOrder, setComponentSortOrder] = useState('highToLow'); // 'highToLow' or 'lowToHigh'
      const [detailedView, setDetailedView] = useState(false); // Toggle for detailed component labels
  const [showPercentagesAboveBars, setShowPercentagesAboveBars] = useState(true); // Toggle for percentage display above bars
  const [roundOffPercentages, setRoundOffPercentages] = useState(false); // Toggle for rounding off percentages
  const [showValuesInTonne, setShowValuesInTonne] = useState(false); // Toggle for displaying values in tonne

  
  // Helper function to convert values to tonnes when the toggle is enabled
  const formatValue = useCallback((value) => {
    if (showValuesInTonne) {
      // Convert kg to tonnes (divide by 1000)
      const tonnes = value / 1000;
      return `${tonnes.toFixed(1)} t CO2-eq`;
    } else {
      // Keep original kg format
      return `${value.toLocaleString()} ${data.category.unit}`;
    }
  }, [showValuesInTonne, data.category.unit]);
  
  // Draggable label overrides (popup only) - separate for each layout
  const [radialLabelPositions, setRadialLabelPositions] = useState({}); // id -> {x,y} for radial layout

  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isDownloading, setIsDownloading] = useState(false);
  const [arrowUpdateTrigger, setArrowUpdateTrigger] = useState(0);
  const chartPanelRef = useRef(null);
  const { toast } = useToast();
  const [showDetailedViewNotification, setShowDetailedViewNotification] = useState(false);
  const lastCloseSignalRef = useRef(closePopupSignal);

  const formatPerM2Yr = useCallback((value) => {
    const area = Number(buildingInfo?.area);
    const life = Number(buildingInfo?.serviceLife);
    if (!Number.isFinite(area) || area <= 0 || !Number.isFinite(life) || life <= 0) return null;
    const per = Number(value || 0) / (area * life);
    if (!Number.isFinite(per)) return null;
    const abs = Math.abs(per);
    const decimals = abs >= 100 ? 1 : abs >= 10 ? 2 : 3;
    const unitLabel = data?.category?.unit ? `${data.category.unit}/m²/yr` : 'per m²/yr';
    return `${per.toFixed(decimals)} ${unitLabel}`;
  }, [buildingInfo, data?.category?.unit]);
  
  // Animation state for notification slide-in/out
  const [notificationAnimation, setNotificationAnimation] = useState(false);
  
  // Handle notification animation and auto-close
  useEffect(() => {
    if (showDetailedViewNotification) {
      // Start with notification off-screen to the right
      setNotificationAnimation(false);
      
      // Trigger slide-in animation after a tiny delay
      setTimeout(() => {
        setNotificationAnimation(true);
      }, 10);
      
      // After 5 seconds, trigger slide-out animation
      const slideOutTimer = setTimeout(() => {
        setNotificationAnimation(false);
      }, 5000);
      
      // After slide-out animation completes, hide the notification
      const hideTimer = setTimeout(() => {
        setShowDetailedViewNotification(false);
      }, 5500); // 5000 + 500ms for exit animation duration
      
      return () => {
        clearTimeout(slideOutTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [showDetailedViewNotification]);

  useEffect(() => {
    if (disableAdvancedControls && detailedView) {
      setDetailedView(false);
      setShowDetailedViewNotification(false);
    }
  }, [disableAdvancedControls, detailedView]);
  
  // Shared dimensions for consistent tooltip sizing
  const tooltipHeight = 'h-[80px]';
  
  // Ref to store the current D3 simulation for control
  const simulationRef = useRef(null);
  
  // Control body scrolling when popup is open
  useEffect(() => {
    if (showPopup) {
      // Prevent body scrolling when popup is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when popup is closed
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPopup]);

  useEffect(() => {
    if (closePopupSignal == null) return;
    if (!showPopup) return;
    if (lastCloseSignalRef.current === closePopupSignal) return;
    lastCloseSignalRef.current = closePopupSignal;
    setShowPopup(false);
    if (onPopupOpenChange) onPopupOpenChange(false);
  }, [closePopupSignal, showPopup, onPopupOpenChange]);

  // Global click handler to deselect when clicking outside the bars
  useEffect(() => {
    const handleGlobalClick = (event) => {
      // Check if click is specifically on a chart segment (bar)
      const isOnChartSegment = event.target.tagName === 'path';
      
      // If click is NOT on a chart segment, deselect
      if (!isOnChartSegment && selectedSegment) {
        setSelectedSegment(null);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [selectedSegment]);
  
  // Check if any label positions have been modified from their original positions
  const hasModifiedPositions = useMemo(() => {
    return Object.keys(radialLabelPositions).length > 0;
  }, [radialLabelPositions]);
  
  const shouldShowPercentages = forcePercentagesAboveBars || showPercentagesAboveBars;

  useEffect(() => {
    if (forcePercentagesAboveBars && !showPercentagesAboveBars) {
      setShowPercentagesAboveBars(true);
    }
  }, [forcePercentagesAboveBars, showPercentagesAboveBars]);

  // Memoize toggle components to reduce dropdown rendering lag
  const percentageToggle = useMemo(() => (
    <div 
      onClick={() => {
        if (!forcePercentagesAboveBars) {
          setShowPercentagesAboveBars(!showPercentagesAboveBars);
        }
      }}
      className={`w-4 h-4 rounded border-2 cursor-pointer transition-all duration-20 ${
        showPercentagesAboveBars 
          ? 'bg-emerald-600 border-emerald-600' 
          : 'bg-white border-emerald-300'
      }`}
    >
      {shouldShowPercentages && (
        <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </div>
  ), [forcePercentagesAboveBars, showPercentagesAboveBars]);
  
  const roundoffToggle = useMemo(() => (
    <div 
      onClick={() => setRoundOffPercentages(!roundOffPercentages)}
      className={`w-4 h-4 rounded border-2 cursor-pointer transition-all duration-20 ${
        roundOffPercentages 
          ? 'bg-emerald-600 border-emerald-600' 
          : 'bg-white border-emerald-300'
      }`}
    >
      {roundOffPercentages && (
        <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </div>
  ), [roundOffPercentages]);
  
  const valuesInTonneToggle = useMemo(() => (
    <div 
      onClick={() => setShowValuesInTonne(!showValuesInTonne)}
      className={`w-4 h-4 rounded border-2 cursor-pointer transition-all duration-20 ${
        showValuesInTonne 
          ? 'bg-emerald-600 border-emerald-600' 
          : 'bg-white border-emerald-300'
      }`}
    >
      {showValuesInTonne && (
        <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </div>
  ), [showValuesInTonne]);
  

  
  // Chart positioning configuration for PDF export - easily tweak these values!
  const CHART_EXPORT_CONFIG = {
    verticalOffset: 180,    // Move chart down by this many pixels (increase = more down)
    horizontalOffset: 70,    // Move chart left/right by this many pixels (positive = right, negative = left)
    horizontalCenter: false  // Set to false if you want manual horizontal positioning
  };
  
  // Function to download the chart as PDF
  const downloadChartAsPDF = async () => {
    if (isDownloading) return; // Prevent multiple downloads
    
    setIsDownloading(true);
    const originalStates = new Map();
    const originalChartStyles = new Map();
    let chartContainer = null;
    try {
      console.log('Starting PDF generation...');
      
      // Import jsPDF dynamically to avoid SSR issues
      const { jsPDF } = await import('jspdf');
      
      console.log('jsPDF imported successfully');
      
      // Prefer the chart container for capture to avoid crop from translated layouts
      const leftPanel = document.querySelector('[data-popup-left-panel]');
      const chartPanel = chartPanelRef.current;
      const panelToCapture = chartPanel || leftPanel;
      if (!panelToCapture) {
        throw new Error('Chart panel not found');
      }
      
      console.log('Capture target found:', panelToCapture);
      
      // Wait for any animations to complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // ULTRA SIMPLE: Just wait for D3 to finish and capture exactly what you see
      console.log('Waiting for D3 simulation to complete...');
      
      if (simulationRef.current) {
        // Let D3 finish naturally - don't force extra ticks
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('D3 simulation should be complete');
      }
      
      // Temporarily hide unwanted UI elements
      const unwantedElements = panelToCapture.querySelectorAll('.absolute.left-3.right-3.bottom-14'); // Bottom tooltip
      const otherUnwanted = panelToCapture.querySelectorAll('[class*="tooltip"], [class*="info"], [class*="generated"]');
      
      // Hide unwanted elements
      [...unwantedElements, ...otherUnwanted].forEach(el => {
        originalStates.set(el, el.style.display);
        el.style.display = 'none';
      });
      
      // Adjust chart position for export (move it down to center it properly)
      chartContainer = chartPanel || panelToCapture.querySelector('[ref="chartPanelRef"]') || panelToCapture.querySelector('.relative.w-\\[500px\\].h-\\[500px\\]');
      if (chartContainer) {
        // Store original positioning
        originalChartStyles.set(chartContainer, {
          transform: chartContainer.style.transform,
          top: chartContainer.style.top,
          position: chartContainer.style.position,
          background: chartContainer.style.background,
          overflow: chartContainer.style.overflow
        });
        
        // Remove transforms for clean capture and ensure white background
        let transformValue = chartContainer === chartPanel
          ? 'none'
          : `translateY(${CHART_EXPORT_CONFIG.verticalOffset}px)`;
        
        if (!CHART_EXPORT_CONFIG.horizontalCenter) {
          // Manual horizontal positioning
          transformValue = chartContainer === chartPanel
            ? 'none'
            : `translate(${CHART_EXPORT_CONFIG.horizontalOffset}px, ${CHART_EXPORT_CONFIG.verticalOffset}px)`;
        }
        
        chartContainer.style.transform = transformValue;
        chartContainer.style.top = '0';
        chartContainer.style.position = 'relative';
        chartContainer.style.background = '#ffffff';
        chartContainer.style.overflow = 'visible';
        
        console.log('Chart position adjusted for export - moved down significantly to prevent top cutoff');
        
        // Log chart container dimensions for debugging
        const chartRect = chartContainer.getBoundingClientRect();
        console.log('Chart container dimensions:', {
          width: chartRect.width,
          height: chartRect.height,
          top: chartRect.top,
          left: chartRect.left,
          transform: chartContainer.style.transform
        });
      }
      
      console.log('Unwanted elements hidden and chart repositioned for capture');
      
      // Create PDF in landscape mode
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      console.log('PDF created, page dimensions:', { pageWidth, pageHeight });
      
      // Clean PDF with just the chart - no header text or boxes
      
      // Now capture the entire left panel as a high-quality image
      let html2canvas;
      try {
        // Try different import methods for compatibility
        const html2canvasModule = await import('html2canvas');
        html2canvas = html2canvasModule.default || html2canvasModule.html2canvas || html2canvasModule;
        console.log('html2canvas imported successfully:', html2canvas);
      } catch (importError) {
        console.error('Failed to import html2canvas:', importError);
        // Try alternative import method
        try {
          const { default: html2canvasDefault } = await import('html2canvas');
          html2canvas = html2canvasDefault;
          console.log('html2canvas imported via default:', html2canvas);
        } catch (altImportError) {
          console.error('Alternative import also failed:', altImportError);
          throw new Error('Failed to load PDF generation library');
        }
      }
      
      if (typeof html2canvas !== 'function') {
        console.error('html2canvas type:', typeof html2canvas);
        console.error('html2canvas value:', html2canvas);
        throw new Error('html2canvas is not available');
      }
      
      let canvas;
      try {
        console.log('Starting html2canvas capture...');
        const rect = panelToCapture.getBoundingClientRect();
        console.log('Panel dimensions:', rect.width, 'x', rect.height);
        
        canvas = await html2canvas(panelToCapture, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: true,
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height),
          scrollX: 0,
          scrollY: 0,
          foreignObjectRendering: false,
          removeContainer: false
        });
        console.log('Canvas created successfully via html2canvas');
      } catch (canvasError) {
        console.error('html2canvas failed:', canvasError);
        throw new Error('Chart capture failed');
      }
      
             // Calculate dimensions to fit the panel properly in the PDF - maximize chart size
       const margin = 10; // mm - reduced margins for bigger chart
       const availableWidth = pageWidth - (2 * margin);
       const availableHeight = pageHeight - (2 * margin); // Use full page height since no header
       
       // Calculate scaling to fit the panel while maintaining aspect ratio
       const scaleX = availableWidth / canvas.width;
       const scaleY = availableHeight / canvas.height;
       const scale = Math.min(scaleX, scaleY);
       
       // Apply additional scaling factor to make chart even bigger
       const additionalScale = 1.2; // 20% bigger
       const finalScale = scale * additionalScale;
       
       const finalWidth = canvas.width * finalScale;
       const finalHeight = canvas.height * finalScale;
       
       // Center the image on the page
       const x = (pageWidth - finalWidth) / 2;
       const y = (pageHeight - finalHeight) / 2; // Center vertically on the full page
      
      console.log('Image dimensions:', { finalWidth, finalHeight, x, y });
      console.log('Page dimensions:', { pageWidth, pageHeight, availableWidth, availableHeight });
      console.log('Chart positioning:', { x, y, margin });
      
      // Add the high-quality image to PDF
      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      
      console.log('Image added to PDF, saving...');
      
      // Download the PDF
      const filename = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      console.log('PDF saved successfully as:', filename);
      
      // Cleanup: Restore original display states and chart positioning
      originalStates.forEach((originalState, element) => {
        element.style.display = originalState;
      });
      
      // Restore original chart positioning
        if (originalChartStyles.has(chartContainer)) {
          const originalStyles = originalChartStyles.get(chartContainer);
          chartContainer.style.transform = originalStyles.transform;
          chartContainer.style.top = originalStyles.top;
          chartContainer.style.position = originalStyles.position;
          chartContainer.style.background = originalStyles.background;
          chartContainer.style.overflow = originalStyles.overflow;
          console.log('Chart positioning restored to original state');
        }
      
      console.log('All original states and positioning restored');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Cleanup: Restore original display states and chart positioning even on error
      if (originalStates) {
        originalStates.forEach((originalState, element) => {
          element.style.display = originalState;
        });
        console.log('Original display states restored after error');
      }
      
      // Restore chart positioning even on error
        if (originalChartStyles && chartContainer) {
          const originalStyles = originalChartStyles.get(chartContainer);
          if (originalStyles) {
            chartContainer.style.transform = originalStyles.transform;
            chartContainer.style.top = originalStyles.top;
            chartContainer.style.position = originalStyles.position;
            chartContainer.style.background = originalStyles.background;
            chartContainer.style.overflow = originalStyles.overflow;
            console.log('Chart positioning restored after error');
          }
        }
      
      // Fallback: show alert if PDF generation fails
      alert(`PDF generation failed: ${error.message}. Please try again.`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Enable dragging of label boxes inside the popup chart panel
  useEffect(() => {
    if (!draggingId) return;
    
    const onMove = (e) => {
      const rect = chartPanelRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const nextX = mouseX - dragOffset.x;
      const nextY = mouseY - dragOffset.y;

      // STEP 2: Use direct DOM manipulation for instant visual feedback
      // Find the label element and move it directly without React state updates
      const labelElement = document.querySelector(`[data-label-id="${draggingId}"]`);
      if (labelElement) {
        labelElement.style.left = `${nextX}px`;
        labelElement.style.top = `${nextY}px`;
      }
    };
    
    const onUp = () => {
      // STEP 3: Re-enable D3 collision simulation after drag ends
      if (simulationRef.current) {
        // Update simulation nodes with current positions
        const nodes = simulationRef.current.nodes();
        if (nodes) {
          nodes.forEach(node => {
            // Update positions from current DOM state
            const labelElement = document.querySelector(`[data-label-id="${node.id}"]`);
            if (labelElement) {
              const currentX = parseFloat(labelElement.style.left);
              const currentY = parseFloat(labelElement.style.top);
              if (!isNaN(currentX) && !isNaN(currentY)) {
                node.labelX = currentX;
                node.labelY = currentY;
                // Update bounding box
                node.bbox.left = currentX - node.bbox.width / 2;
                node.bbox.right = currentX + node.bbox.width / 2;
                node.bbox.top = currentY - node.bbox.height / 2;
                node.bbox.bottom = currentY + node.bbox.height / 2;
              }
            }
          });
        }
        
        // Restart simulation with fresh alpha for smooth animation
        simulationRef.current.alpha(1).restart();
        
        // Run simulation for a few ticks to resolve collisions
        for (let i = 0; i < 100; ++i) {
          simulationRef.current.tick();
        }
        
        // Update visual positions with final simulation results
        const finalNodes = simulationRef.current.nodes();
        if (finalNodes) {
          finalNodes.forEach(node => {
            const labelElement = document.querySelector(`[data-label-id="${node.id}"]`);
            if (labelElement) {
              labelElement.style.left = `${node.labelX}px`;
              labelElement.style.top = `${node.labelY}px`;
            }
          });
        }
        
        // Stop simulation after collision resolution
        simulationRef.current.stop();
        
        // Trigger arrow re-render after simulation updates
        setArrowUpdateTrigger(prev => prev + 1);
      }
      
      // STEP 4: Let D3 reposition the label to its final collision-free position
      // Get final position from DOM and update React state
      const labelElement = document.querySelector(`[data-label-id="${draggingId}"]`);
      if (labelElement) {
        const finalX = parseFloat(labelElement.style.left);
        const finalY = parseFloat(labelElement.style.top);
        
        // Update the simulation data with the final position
        if (simulationRef.current && simulationRef.current.nodes()) {
          const nodes = simulationRef.current.nodes();
          const draggedNode = nodes.find(node => node.id === draggingId);
          if (draggedNode) {
            draggedNode.labelX = finalX;
            draggedNode.labelY = finalY;
            // Update bounding box
            draggedNode.bbox.left = finalX - draggedNode.bbox.width / 2;
            draggedNode.bbox.right = finalX + draggedNode.bbox.width / 2;
            draggedNode.bbox.top = finalY - draggedNode.bbox.height / 2;
            draggedNode.bbox.bottom = finalY + draggedNode.bbox.height / 2;
          }
        }
        
        setRadialLabelPositions((prev) => ({ ...prev, [draggingId]: { x: finalX, y: finalY } }));
        
        // Trigger arrow re-render to update positions
        setArrowUpdateTrigger(prev => prev + 1);
      }
      
      setDraggingId(null);
    };
    
    // STEP 1: Disable D3 collision simulation during drag
    if (simulationRef.current) {
      simulationRef.current.alpha(0).stop();
    }
    
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, dragOffset, setRadialLabelPositions]);
  
  const getCategoryColor = (category, index) => {
    const colors = {
        'Finishes': '#FF0A54',
        'Structure': '#012A4A',
        'Envelope': '#155D27',
        'MEP': '#212529',
        'Other': '#DC2626', // Red for Other category
        'Others': '#DC2626' // Also red for Others (plural)
    };
    return colors[category] || `hsl(${index * 45}, 65%, 55%)`;
  };
  
  const getComponentColor = (category, index) => {
      const gradients = {
        'Finishes': ['#FF0A54', '#FF477E', '#FF5C8A', '#FF7096', '#FF85A1', '#FF99AC', '#FBB1BD', '#F9BEC7', '#F7CAD0', '#FAE0E4'],
        'Structure': ['#012A4A', '#013A63', '#014F86', '#2A6F97', '#2C7DA0', '#61A5C2', '#89C2D9', '#A9D6E5'],
        'Envelope': ['#155D27', '#1A7431', '#208B3A', '#25A244', '#2DC653', '#4AD66D', '#6EDE8A', '#92E6A7', '#B7EFC5'],
        'MEP': ['#343A40', '#6C757D', '#ADB5BD', '#CED4DA', '#DEE2E6', '#E9ECEF'],
        'Other': ['#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2', '#FEF2F2'], // Red gradients starting from main color
        'Others': ['#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2', '#FEF2F2'] // Red gradients starting from main color
      };
      
      // Debug: Log the category and available gradients
      console.log(`Category: ${category}, Available gradients:`, Object.keys(gradients));
      
      const categoryGradients = gradients[category] || gradients['Other'];
      return categoryGradients[index % categoryGradients.length];
  };
  
  // Generate data for inner ring (categories)
  const generateCategoryData = (centerX, centerY, radius, innerRadius) => {
    if (Array.isArray(categoryDataOverride) && categoryDataOverride.length > 0) {
      const total = Number(data?.total || 0);
      const baseTotal = total > 0
        ? total
        : categoryDataOverride.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
      const mapped = categoryDataOverride.map((item, index) => {
        const value = Number(item.value) || 0;
        const percentage = Number.isFinite(item.percentage)
          ? Number(item.percentage)
          : baseTotal > 0
            ? (value / baseTotal) * 100
            : 0;
        return {
          category: item.category || item.name || `Slice ${index + 1}`,
          value,
          percentage,
          color: item.color || getCategoryColor(item.category || item.name || 'Other', index)
        };
      });

      let cumulativeAngle = 0;
      const totalPercentage = mapped.reduce((sum, item) => sum + item.percentage, 0);
      const scaleFactor = totalPercentage > 100 ? 100 / totalPercentage : 1;

      return mapped.map((item, index) => {
        const percentage = item.percentage * scaleFactor;
        const angle = (percentage / 100) * 360;
        const startAngle = cumulativeAngle;
        const endAngle = index === mapped.length - 1 ? 360 : cumulativeAngle + angle;
        cumulativeAngle += angle;

        const startAngleRad = (startAngle - 90) * (Math.PI / 180);
        const endAngleRad = (endAngle - 90) * (Math.PI / 180);

        const x1 = centerX + Math.cos(startAngleRad) * radius;
        const y1 = centerY + Math.sin(startAngleRad) * radius;
        const x2 = centerX + Math.cos(endAngleRad) * radius;
        const y2 = centerY + Math.sin(endAngleRad) * radius;

        const x3 = centerX + Math.cos(endAngleRad) * innerRadius;
        const y3 = centerY + Math.sin(endAngleRad) * innerRadius;

        const x4 = centerX + Math.cos(startAngleRad) * innerRadius;
        const y4 = centerY + Math.sin(startAngleRad) * innerRadius;

        const largeArcFlag = angle > 180 ? 1 : 0;

        const pathData = [
          `M ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          `L ${x3} ${y3}`,
          `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
          'Z'
        ].join(' ');

        return {
          ...item,
          pathData,
          startAngle,
          endAngle,
          type: 'category'
        };
      });
    }

    if (!componentsByCategory) return [];
    
    // Define the fixed order for main categories
    const mainCategories = ['Structure', 'Envelope', 'Finishes', 'MEP'];
    
    // Group components by main categories and put others in "Others"
    const organizedCategories = {};
    const othersComponents = [];
    
    Object.entries(componentsByCategory).forEach(([category, components]) => {
      if (mainCategories.includes(category)) {
        organizedCategories[category] = components;
      } else {
        // Add components from non-main categories to "Others"
        othersComponents.push(...components);
      }
    });
    
    // Add "Others" category if there are components that don't fit main categories
    if (othersComponents.length > 0) {
      organizedCategories['Others'] = othersComponents;
    }
    
    // Create category data in the fixed order
    const categoryData = mainCategories
      .filter(category => organizedCategories[category])
      .map(category => {
        const components = organizedCategories[category];
        const totalValue = components.reduce((sum, comp) => sum + (comp.impacts[data.category.id] || 0), 0);
        const percentage = (totalValue / data.total) * 100;
        return { category, value: totalValue, percentage };
      });
    
    // Add "Others" at the end if it exists
    if (organizedCategories['Others']) {
      const othersValue = organizedCategories['Others'].reduce((sum, comp) => sum + (comp.impacts[data.category.id] || 0), 0);
      const othersPercentage = (othersValue / data.total) * 100;
      categoryData.push({ category: 'Others', value: othersValue, percentage: othersPercentage });
    }

    let cumulativeAngle = 0;
    
    // Ensure total percentage doesn't exceed 100% to prevent overlapping
    const totalPercentage = categoryData.reduce((sum, item) => sum + item.percentage, 0);
    const scaleFactor = totalPercentage > 100 ? 100 / totalPercentage : 1;
    
    return categoryData.map((item, index) => {
      const percentage = item.percentage * scaleFactor;
      const angle = (percentage / 100) * 360;
      const startAngle = cumulativeAngle;
      // Ensure the last segment doesn't exceed 360 degrees
      const endAngle = index === categoryData.length - 1 ? 360 : cumulativeAngle + angle;
      cumulativeAngle += angle;
      
      const startAngleRad = (startAngle - 90) * (Math.PI / 180);
      const endAngleRad = (endAngle - 90) * (Math.PI / 180);
      
      const x1 = centerX + Math.cos(startAngleRad) * radius;
      const y1 = centerY + Math.sin(startAngleRad) * radius;
      const x2 = centerX + Math.cos(endAngleRad) * radius;
      const y2 = centerY + Math.sin(endAngleRad) * radius;
      
      const x3 = centerX + Math.cos(endAngleRad) * innerRadius;
      const y3 = centerY + Math.sin(endAngleRad) * innerRadius;
      const x4 = centerX + Math.cos(startAngleRad) * innerRadius;
      const y4 = centerY + Math.sin(startAngleRad) * innerRadius;
      
      const largeArcFlag = angle > 180 ? 1 : 0;
      
      const pathData = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
        'Z'
      ].join(' ');
      
      return {
        ...item,
        pathData,
        startAngle,
        endAngle,
        color: getCategoryColor(item.category, index),
        type: 'category'
      };
    });
  };

  // Generate data for outer ring (components) - aligned with categories
  const generateComponentData = (centerX, centerY, radius, innerRadius) => {
    if (!componentsByCategory) return [];
    
    // Define the fixed order for main categories (same as inner ring)
    const mainCategories = ['Structure', 'Envelope', 'Finishes', 'MEP'];
    
    // Group components by main categories and put others in "Others"
    const organizedCategories = {};
    const othersComponents = [];
    
    Object.entries(componentsByCategory).forEach(([category, components]) => {
      if (mainCategories.includes(category)) {
        organizedCategories[category] = components;
      } else {
        // Add components from non-main categories to "Others"
        othersComponents.push(...components);
      }
    });
    
    // Add "Others" category if there are components that don't fit main categories
    if (othersComponents.length > 0) {
      organizedCategories['Others'] = othersComponents;
    }
    
    // Create category order based on the fixed sequence
    const categoryOrder = mainCategories
      .filter(category => organizedCategories[category])
      .concat(organizedCategories['Others'] ? ['Others'] : []);
    
    const allComponents = [];
    categoryOrder.forEach(category => {
      const categoryComponents = organizedCategories[category];
      categoryComponents.forEach(component => {
        const value = component.impacts[data.category.id] || 0;
        const percentage = (value / data.total) * 100;
        allComponents.push({
          ...component,
          category,
          value,
          percentage
        });
      });
    });
    
    // Sort components within each category by value
    const sortedComponents = [];
    categoryOrder.forEach(category => {
      const categoryComponents = allComponents.filter(comp => comp.category === category);
      categoryComponents.sort((a, b) => b.value - a.value);
      sortedComponents.push(...categoryComponents);
    });
    
    let cumulativeAngle = 0;
    
    // Ensure total percentage doesn't exceed 100% to prevent overlapping
    const totalPercentage = sortedComponents.reduce((sum, item) => sum + item.percentage, 0);
    const scaleFactor = totalPercentage > 100 ? 100 / totalPercentage : 1;
    
    // Track index within each category for proper gradient progression
    const categoryIndices = {};
    categoryOrder.forEach(cat => categoryIndices[cat] = 0);
    
    return sortedComponents.map((item, globalIndex) => {
      const percentage = item.percentage * scaleFactor;
      const angle = (percentage / 100) * 360;
      const startAngle = cumulativeAngle;
      // Ensure the last segment doesn't exceed 360 degrees
      const endAngle = globalIndex === sortedComponents.length - 1 ? 360 : cumulativeAngle + angle;
      cumulativeAngle += angle;
      
      const startAngleRad = (startAngle - 90) * (Math.PI / 180);
      const endAngleRad = (endAngle - 90) * (Math.PI / 180);
      
      const x1 = centerX + Math.cos(startAngleRad) * radius;
      const y1 = centerY + Math.sin(startAngleRad) * radius;
      const x2 = centerX + Math.cos(endAngleRad) * radius;
      const y2 = centerY + Math.sin(endAngleRad) * radius;
      
      const x3 = centerX + Math.cos(endAngleRad) * innerRadius;
      const y3 = centerY + Math.sin(endAngleRad) * innerRadius;
      const x4 = centerX + Math.cos(startAngleRad) * innerRadius;
      const y4 = centerY + Math.sin(startAngleRad) * innerRadius;
      
      const largeArcFlag = angle > 180 ? 1 : 0;
      
      const pathData = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
        'Z'
      ].join(' ');
      
      // Get the index within this specific category for proper gradient progression
      const categoryIndex = categoryIndices[item.category];
      categoryIndices[item.category]++;
      
      // Debug: Log component category and color assignment
      const assignedColor = getComponentColor(item.category, categoryIndex);
      console.log(`Component: ${item.name}, Category: ${item.category}, Category Index: ${categoryIndex}, Color: ${assignedColor}`);
      
      return {
        ...item,
        pathData,
        startAngle,
        endAngle,
        color: assignedColor,
        type: 'component'
      };
    });
  };



  const chartData = useMemo(() => {
    const useOuter = Array.isArray(categoryDataOverride) && categoryDataOverride.length > 0;
    const categoryData = useOuter
      ? generateCategoryData(200, 200, 160, 110)
      : generateCategoryData(200, 200, 110, 80);
    const componentData = useOuter ? [] : generateComponentData(200, 200, 160, 110);
    return { categoryData, componentData };
  }, [data, componentsByCategory, categoryDataOverride]);

  const popupChartData = useMemo(() => {
    const useOuter = Array.isArray(categoryDataOverride) && categoryDataOverride.length > 0;
    const categoryData = useOuter
      ? generateCategoryData(250, 250, 160, 110)
      : generateCategoryData(250, 250, 110, 82.5);
    const componentData = useOuter ? [] : generateComponentData(250, 250, 160, 110);
    return { categoryData, componentData };
  }, [data, componentsByCategory, categoryDataOverride]);

  const hasCategoryOverride = Array.isArray(categoryDataOverride) && categoryDataOverride.length > 0;
  const explodeCategoryOutward = hasCategoryOverride;
  const embeddedCategoryRadii = hasCategoryOverride
    ? { outer: 160, inner: 110 }
    : { outer: 110, inner: 80 };
  const popupCategoryRadii = hasCategoryOverride
    ? { outer: 160, inner: 110 }
    : { outer: 110, inner: 82.5 };

  const handleSegmentClick = (segment) => {
    // If clicking the same segment, deselect it
    if (selectedSegment && selectedSegment.type === segment.type && 
        ((segment.type === 'category' && selectedSegment.category === segment.category) ||
         (segment.type === 'component' && selectedSegment.name === segment.name))) {
      setSelectedSegment(null);
    } else {
      setSelectedSegment(segment);
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
    if (onPopupOpenChange) onPopupOpenChange(true);
  };

  const handleClosePopup = () => {
    if (onPopupCloseRequest && onPopupCloseRequest() === false) {
      return;
    }
    setShowPopup(false);
    if (onPopupOpenChange) onPopupOpenChange(false);
  };

  return (
    <>
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md" data-chart-card>
      <CardHeader>
          <div className="flex items-center justify-between">
        <CardTitle className="flex items-center text-gray-800">
          <PieChart className="w-5 h-5 mr-2 text-emerald-600" />
          {title}
        </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenPopup}
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
      </CardHeader>
      <CardContent className={`pb-6 ${contentClassName}`}>
          <div className="flex flex-col items-center justify-center">
            {/* Interactive Concentric Donut Chart */}
          <div className="relative w-full flex justify-center" style={{ height: '440px' }} onClick={(e) => {
            // Only deselect if clicking on the container background, not on SVG elements
            if (e.target === e.currentTarget) {
              setSelectedSegment(null);
            }
          }}>
              <div className={`origin-center -translate-y-10 ${mobileChartScale}`}>
                <svg
                  width="400"
                  height="400"
                  viewBox="0 0 400 400"
                >
                {/* Inner ring - Categories */}
                {chartData.categoryData.map((segment, index) => (
                  <g key={`category-${segment.category}`}>
                    <path
                      d={segment.pathData}
                      fill={segment.color}
                      stroke="white"
                      strokeWidth="1"
                        className={`cursor-pointer transition-transform duration-20 ${
                          selectedSegment?.category === segment.category && selectedSegment?.type === 'category'
                            ? 'opacity-100' 
                            : 'opacity-100 hover:opacity-100'
                        }`}
                        style={{
                          transformOrigin: '200px 200px',
                          transform: selectedSegment?.category === segment.category && selectedSegment?.type === 'category'
                            ? (explodeCategoryOutward ? 'scale(1.05)' : 'scale(0.97)')
                            : 'scale(1)',
                          transition: 'transform 20ms ease-out, opacity 20ms ease-out, filter 20ms ease-out'
                        }}
                       onClick={() => handleSegmentClick(segment)}
                    />
                    
                    {/* Percentage text above category segments when percentage display is enabled */}
                    {shouldShowPercentages && (() => {
                      // Only show percentage for segments large enough to fit text
                      const segmentSize = segment.endAngle - segment.startAngle;
                      const minSegmentSize = 10; // Minimum angle in degrees to show percentage
                      
                      if (segmentSize < minSegmentSize) {
                        return null; // Don't show percentage for very small segments
                      }
                      
                      // Calculate the exact center of the segment
                      const midAngle = (segment.startAngle + segment.endAngle) / 2;
                      const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                      
                      // Position percentage text exactly in the center of the bar
                      const outerRadius = embeddedCategoryRadii.outer;
                      const innerRadius = embeddedCategoryRadii.inner;
                      const middleRadius = (outerRadius + innerRadius) / 2; // Exact center of the bar
                      const textX = 200 + Math.cos(midAngleRad) * middleRadius;
                      const textY = 200 + Math.sin(midAngleRad) * middleRadius;
                      
                      return (
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-sm font-semibold fill-white pointer-events-none"
                          style={{
                            fontSize: '10px',
                            fontWeight: '500',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                          }}
                        >
                          {roundOffPercentages ? Math.round(segment.percentage) : segment.percentage.toFixed(0)}%
                        </text>
                      );
                    })()}
                  </g>
                ))}

                {/* Outer ring - Components */}
                {chartData.componentData.map((segment, index) => (
                  <g key={`component-${segment.name}`}>
                  <path
                    d={segment.pathData}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth="1"
                                                                                                                                     className={`cursor-pointer transition-transform duration-20 ${
                        selectedSegment?.name === segment.name && selectedSegment?.type === 'component'
                          ? 'opacity-100 scale-105' 
                          : 'opacity-100 hover:opacity-100'
                      }`}
                       style={{
                         transformOrigin: '200px 200px',
                         transform: selectedSegment?.name === segment.name && selectedSegment?.type === 'component' ? 'scale(1.05)' : 'scale(1)',
                         transition: 'transform 20ms ease-out, opacity 20ms ease-out, filter 20ms ease-out'
                       }}
                       onClick={() => handleSegmentClick(segment)}
                    />
                    
                    {/* Percentage text above component segments when percentage display is enabled */}
                    {shouldShowPercentages && (() => {
                      // Only show percentage for segments large enough to fit text
                      const segmentSize = segment.endAngle - segment.startAngle;
                      const minSegmentSize = 8; // Minimum angle in degrees to show percentage
                      
                      if (segmentSize < minSegmentSize) {
                        return null; // Don't show percentage for very small segments
                      }
                      
                      // Calculate the exact center of the segment
                      const midAngle = (segment.startAngle + segment.endAngle) / 2;
                      const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                      
                      // Position percentage text exactly in the center of the bar
                      const outerRadius = 160; // Outer radius of the component ring (matches chartData)
                      const innerRadius = 110; // Inner radius of the component ring (matches chartData)
                      const middleRadius = (outerRadius + innerRadius) / 2; // Exact center of the bar
                      const textX = 200 + Math.cos(midAngleRad) * middleRadius;
                      const textY = 200 + Math.sin(midAngleRad) * middleRadius;
                      
                      return (
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-sm font-semibold fill-white pointer-events-none"
                          style={{
                            fontSize: '10px',
                            fontWeight: '500',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                          }}
                        >
                          {roundOffPercentages ? Math.round(segment.percentage) : segment.percentage.toFixed(1)}%
                        </text>
                      );
                    })()}
                  </g>
                ))}
                
                {/* Center text */}
                <text
                  x="200"
                  y="195"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-3xl font-bold fill-gray-800"
                  style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
                >
                  {buildingInfo ? ((data.total / buildingInfo.area) / buildingInfo.serviceLife).toFixed(2) : data.total.toLocaleString()}
                </text>
                <text
                  x="200"
                  y="220"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-sm fill-gray-600"
                  style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
                >
                  kg CO2-eq/m²/year
                </text>
                </svg>
              </div>
            </div>
            
            {/* Horizontal Legend - Above the tooltip */}
            <div
              className="absolute bottom-14 left-4 right-4 flex justify-center -translate-y-16 sm:-translate-y-20"
              onClick={() => setSelectedSegment(null)}
              data-legend
            >
              <div className="flex space-x-6">
                {chartData.categoryData.map((segment) => (
                  <div key={`legend-${segment.category}`} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: segment.color }}
                    />
                    <span className="text-xs text-gray-600 font-medium">{segment.category}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Click Tooltip - Absolutely positioned at bottom of existing card area */}
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 bg-gray-800/95 text-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg text-center shadow-md text-[11px] sm:text-xs leading-snug" onClick={() => setSelectedSegment(null)} data-tooltip>
                {selectedSegment ? (
                  <div className="w-full">
                    <div className="font-semibold text-emerald-400 text-[11px] sm:text-xs mb-1">
                    {selectedSegment.type === 'category' ? selectedSegment.category : selectedSegment.name}
                  </div>
                    <div className="space-y-0.5">
                      <div>
                        {formatValue(selectedSegment.value)}
                        {formatPerM2Yr(selectedSegment.value)
                          ? ` • ${formatPerM2Yr(selectedSegment.value)}`
                          : ''}
                      </div>
                    <div>{selectedSegment.percentage.toFixed(1)}% of total</div>
                    {selectedSegment.type === 'component' && (
                      <div className="text-gray-300">Category: {selectedSegment.category}</div>
                    )}
                    {selectedSegment.type === 'category' && (
                      <div className="text-gray-300">Category Group</div>
                    )}
                  </div>
                </div>
                ) : (
                  <div className="text-white text-xs">
                    Click any bar in the chart to view the elements details!
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Popup Modal - Rendered outside the Card */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full h-full sm:w-[95vw] sm:h-[95vh] overflow-y-auto lg:overflow-hidden">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 py-3 sm:px-4 border-b border-gray-200">
              <div className="flex items-center justify-between gap-2 sm:gap-3 sm:flex-1">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <PieChart className="w-4 h-4 mr-2 text-emerald-600" />
                  {title}
                </h2>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleClosePopup}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 w-7 p-0 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 w-full place-items-stretch sm:w-auto sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                {!disableAdvancedControls && (
                  <Button
                    variant={detailedView ? 'default' : 'outline'}
                    size="xs"
                    onClick={() => {
                      const newDetailedView = !detailedView;
                      setDetailedView(newDetailedView);

                      // Show notification and reset arrow behavior when switching to Detailed View
                      if (newDetailedView) {
                        setArrowUpdateTrigger(prev => prev + 1);
                        setShowDetailedViewNotification(true);
                      }
                    }}
                    className={(detailedView ? 'bg-emerald-600 hover:bg-emerald-700 text-white ' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 ') + 'h-7 px-2 w-full sm:w-auto'}
                  >
                    {detailedView ? 'Simple View' : 'Detailed View'}
                  </Button>
                )}
                {detailedView && (

                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => {
                        setRadialLabelPositions({});
                        
                        // Trigger arrow re-render after reset
                        setArrowUpdateTrigger(prev => prev + 1);
                      }}
                      disabled={!hasModifiedPositions}
                      className={`border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 h-7 px-2 w-full sm:w-auto ${
                        !hasModifiedPositions ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Reset Radial Layout Positions"
                    >
                      Reset Positions
                    </Button>
                )}
                
                {/* Prominent notification when Detailed View is activated */}
                  {!disableAdvancedControls && showDetailedViewNotification && (
                    <div
                      className="fixed top-4 left-4 right-4 sm:top-20 sm:left-auto sm:right-20 max-w-full sm:max-w-lg z-[9999] transform transition-transform duration-500 ease-out"
                      style={{
                        transform: notificationAnimation ? 'translateX(0)' : 'translateX(100%)',
                        transition: notificationAnimation
                          ? 'transform 700ms cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                          : 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      }}
                    >
                      <div className="rounded-2xl border border-[#d5dacc] bg-white px-5 py-4 shadow-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1">
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6b7f73]">
                              Detailed View
                            </div>
                            <div className="text-sm font-semibold text-[#2b4331] leading-relaxed">
                              Please drag around the labels to align with the bars.
                            </div>
                          </div>
                          <button
                            onClick={() => setShowDetailedViewNotification(false)}
                            className="text-[#4f6b5a] hover:text-[#2b4331] hover:bg-[#f1f4ec] rounded-full p-2 transition-colors flex-shrink-0"
                            title="Close notification"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                
                
                {/* Edit Values Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="xs"
                      className="h-7 px-3 w-full sm:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"
                    >
                      Edit Values
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48" style={{ animationDuration: '20ms' }}>
                    <DropdownMenuItem className="flex items-center justify-between cursor-default" onClick={(e) => e.preventDefault()}>
                      <span>Percentage Above Bars</span>
                      {percentageToggle}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center justify-between cursor-default" onClick={(e) => e.preventDefault()}>
                      <span>Roundoff Percentages</span>
                      {roundoffToggle}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center justify-between cursor-default" onClick={(e) => e.preventDefault()}>
                      <span>Values in tonne</span>
                      {valuesInTonneToggle}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button
                  variant="outline"
                  size="xs"
                  disabled
                  className="h-7 px-3 w-full sm:w-auto border-emerald-200 text-emerald-700 opacity-50 cursor-not-allowed"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Chart
                </Button>
              </div>
            </div>
            
            <div className="flex flex-col lg:flex-row h-full relative overflow-y-auto lg:overflow-hidden">
              {/* PDF Generation Overlay - covers only the popup content area */}
              {isDownloading && (
                <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-40 flex flex-col items-center justify-center rounded-lg border-2 border-emerald-200 shadow-xl">
                  {/* Industrial Carbon Process Visualization */}
                  <div className="relative mb-8">
                    {/* Main Industrial Container */}
                    <div className="w-24 h-20 bg-gradient-to-b from-emerald-100 to-emerald-200 rounded-lg border-2 border-emerald-300 shadow-lg relative overflow-hidden">
                      {/* Carbon Emission Flow Lines */}
                      <div className="absolute inset-0">
                        {/* Horizontal flow lines */}
                        <div className="absolute top-2 left-0 w-full h-0.5 bg-emerald-400 animate-pulse"></div>
                        <div className="absolute top-4 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.3s'}}></div>
                        <div className="absolute top-6 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.6s'}}></div>
                        <div className="absolute top-8 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '0.9s'}}></div>
                        <div className="absolute top-10 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{animationDelay: '1.2s'}}></div>
                      </div>
                      
                      {/* Vertical processing columns */}
                      <div className="absolute left-2 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse"></div>
                      <div className="absolute left-6 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      <div className="absolute left-10 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.8s'}}></div>
                      <div className="absolute left-14 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.2s'}}></div>
                      <div className="absolute left-18 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '1.6s'}}></div>
                      <div className="absolute left-22 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
                      
                      {/* Carbon particles flowing through */}
                      <div className="absolute top-1 left-1 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
                      <div className="absolute top-3 left-5 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      <div className="absolute top-5 left-9 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                      <div className="absolute top-7 left-13 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.6s'}}></div>
                      <div className="absolute top-9 left-17 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.8s'}}></div>
                      <div className="absolute top-11 left-21 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
                    </div>
                    
                    {/* Industrial Pipes and Connections */}
                    <div className="absolute -top-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
                    <div className="absolute -top-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute -bottom-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                    <div className="absolute -bottom-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
                    
                    {/* Connection Nodes */}
                    <div className="absolute -top-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping"></div>
                    <div className="absolute -top-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute -bottom-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
                    <div className="absolute -bottom-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{animationDelay: '1.5s'}}></div>
                    
                    {/* Carbon Emission Clouds */}
                    <div className="absolute -top-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse"></div>
                    <div className="absolute -top-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '0.7s'}}></div>
                    <div className="absolute -bottom-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '1.4s'}}></div>
                    <div className="absolute -bottom-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{animationDelay: '2.1s'}}></div>
                  </div>
                  
                  {/* Industrial Process Text */}
                  <div className="text-xl font-semibold text-emerald-700 mb-3 animate-pulse">
                    Processing Carbon Data
                  </div>
                  
                  {/* Process Status Indicators */}
                  <div className="flex space-x-3 mb-4">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                      <div className="text-xs text-emerald-600 mt-1">Collecting</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
                      <div className="text-xs text-emerald-600 mt-1">Analyzing</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                      <div className="text-xs text-emerald-600 mt-1">Compiling</div>
                    </div>
                  </div>
                  
                  {/* Industrial Subtitle */}
                  <div className="text-base text-emerald-600 text-center max-w-sm animate-pulse">
                    Well, wait for sometime untill I process the data...
                  </div>
                </div>
              )}
              {/* Left side - Concentric Donut Chart with proper boundaries */}
                <div className="relative w-full lg:w-3/5 flex flex-col items-center justify-center p-3 sm:p-6" data-popup-left-panel>
                {/* Chart container with expanded height and positioned above center to avoid tooltip overlap */}
                <div
                  ref={chartPanelRef}
                  className="relative w-full max-w-[320px] sm:max-w-[460px] lg:max-w-[500px] aspect-[4/5] sm:aspect-square flex-shrink-0 flex items-center justify-center -translate-y-1 sm:-translate-y-4 lg:-translate-y-[70px]"
                >
                  <svg width="100%" height="100%" viewBox="0 0 500 500">
                    {/* Inner ring - Categories */}
                    {popupChartData.categoryData.map((segment, index) => (
                      <g key={`category-${segment.category}`}>
                        <path
                          d={segment.pathData}
                          fill={segment.color}
                          stroke="white"
                          strokeWidth="1"
                           className={`cursor-pointer transition-all duration-20 ${
                              selectedSegment?.category === segment.category && selectedSegment?.type === 'category'
                                ? 'opacity-100' 
                                : 'opacity-100 hover:opacity-100'
                            }`}
                           style={{
                             transformOrigin: '250px 250px',
                             transform: selectedSegment?.category === segment.category && selectedSegment?.type === 'category'
                               ? (explodeCategoryOutward ? 'scale(1.05)' : 'scale(0.97)')
                               : 'scale(1)',
                             transition: 'transform 20ms ease-out, opacity 20ms ease-out, filter 20ms ease-out'
                           }}
                           onClick={() => handleSegmentClick(segment)}
                        />
                        
                        {/* Percentage text above category segments when percentage display is enabled */}
                        {shouldShowPercentages && (() => {
                          // Only show percentage for segments large enough to fit text
                          const segmentSize = segment.endAngle - segment.startAngle;
                          const minSegmentSize = 10; // Minimum angle in degrees to show percentage
                          
                          if (segmentSize < minSegmentSize) {
                            return null; // Don't show percentage for very small segments
                          }
                          
                          // Calculate the exact center of the segment
                          const midAngle = (segment.startAngle + segment.endAngle) / 2;
                          const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                          
                          // Position percentage text in the middle of the bar (not on the outer edge)
                          const outerRadius = popupCategoryRadii.outer;
                          const innerRadius = popupCategoryRadii.inner;
                          const middleRadius = (outerRadius + innerRadius) / 2; // Middle of the bar
                          const textX = 250 + Math.cos(midAngleRad) * middleRadius;
                          const textY = 250 + Math.sin(midAngleRad) * middleRadius;
                          
                          return (
                            <text
                              x={textX}
                              y={textY}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className="text-sm font-semibold fill-white pointer-events-none"
                              style={{
                                fontSize: '10px',
                                fontWeight: '500',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none'
                              }}
                            >
                              {Math.round(segment.percentage)}%
                            </text>
                          );
                        })()}
                      </g>
                    ))}

                    {/* Outer ring - Components */}
                    {popupChartData.componentData.map((segment, index) => (
                      <g key={`component-${segment.name}`}>
                        <path
                          d={segment.pathData}
                          fill={segment.color}
                          stroke="white"
                          strokeWidth="1"
                                                                                                                                                                                                                                            className={`cursor-pointer transition-all duration-20 ${
                           selectedSegment?.name === segment.name && selectedSegment?.type === 'component'
                             ? 'opacity-100 scale-105' 
                             : 'opacity-100 hover:opacity-100'
                         }`}
                         style={{
                           transformOrigin: '250px 250px',
                           transform: selectedSegment?.name === segment.name && selectedSegment?.type === 'component' ? 'scale(1.05)' : 'scale(1)',
                            transition: 'transform 20ms ease-out, opacity 20ms ease-out, filter 20ms ease-out'
                         }}
                           onClick={() => handleSegmentClick(segment)}
                  />
                        
                        {/* Percentage text above chart segments when percentage display is enabled */}
                        {shouldShowPercentages && (() => {
                          // Only show percentage for segments large enough to fit text
                          const segmentSize = segment.endAngle - segment.startAngle;
                          const minSegmentSize = 8; // Minimum angle in degrees to show percentage
                          
                          if (segmentSize < minSegmentSize) {
                            return null; // Don't show percentage for very small segments
                          }
                          
                          // Calculate the exact center of the segment
                          const midAngle = (segment.startAngle + segment.endAngle) / 2;
                          const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                          
                          // Position percentage text in the middle of the bar (not on the outer edge)
                          const outerRadius = 140; // Outer radius of the component ring
                          const innerRadius = 130; // Inner radius of the component ring
                          const middleRadius = (outerRadius + innerRadius) / 2; // Middle of the bar
                          const textX = 250 + Math.cos(midAngleRad) * middleRadius;
                          const textY = 250 + Math.sin(midAngleRad) * middleRadius;
                          
                          return (
                            <text
                              x={textX}
                              y={textY}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className="text-sm font-semibold fill-white pointer-events-none"
                              style={{
                                fontSize: '10px',
                                fontWeight: '500',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none'
                              }}
                            >
                              {roundOffPercentages ? Math.round(segment.percentage) : segment.percentage.toFixed(1)}%
                            </text>
                          );
                        })()}
                </g>
              ))}
                    
                    {/* Removed chart arrow marker - no longer needed */}
                    
                    {/* Removed redundant simple chart arrows - only keeping the actual label arrows */}
              
              {/* Center text */}
              <text
                    x="250"
                    y="250"
                textAnchor="middle"
                      className="text-3xl font-bold fill-gray-800"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
              >
                {buildingInfo ? ((data.total / buildingInfo.area) / buildingInfo.serviceLife).toFixed(2) : data.total.toLocaleString()}
              </text>
              <text
                    x="250"
                    y="270"
                textAnchor="middle"
                      className="text-sm fill-gray-600"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
              >
                    kg CO2-eq/m²/year
              </text>
              
              {/* Removed blue boundary box */}
            </svg>
            
                                     {/* Radial Labels with D3-Force Collision Avoidance - Only shown in detailed view */}
                   {detailedView && (() => {
                    // Import D3 for force simulation
                    const d3 = window.d3;
                    
                    // Radial label positioning with D3-force collision avoidance
                    const chartCenter = { x: 250, y: 250 };
                    const donutRadius = 150; // Outer radius of donut
                    const margin = 100; // Increased margin to ensure no overlap with chart
                    const baseRadius = donutRadius + margin; // Start at minimum safe distance (250px)
                    const labelHeight = 30; // Reduced height for single-line layout
                    const padding = 20; // Reduced padding for better spacing
                    
                    // Generate initial label positions based on layout type
                    const detailedSegments = popupChartData.componentData.length
                      ? popupChartData.componentData
                      : popupChartData.categoryData;
                    const getSegmentLabel = (segment) => segment.name || segment.category || 'Segment';
                    let labels;
                    
                    if (false) { // BOX LAYOUT DISABLED - ALWAYS USE RADIAL LAYOUT
                      // Box layout: arrange labels in fixed boxes around the chart (top, left, bottom, right)
                      // No collision resolution needed since labels are positioned in fixed locations
                      const boxLabels = detailedSegments
                        .sort((a, b) => b.percentage - a.percentage) // Sort by percentage for better organization
                        .map((segment, index) => {
                          const midAngle = (segment.startAngle + segment.endAngle) / 2;
                          const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                          
                          // Anchor point on the donut outer edge with slight offset to prevent overlapping
                          const anchorOffset = 10; // Small offset to prevent arrow overlap
                          const anchorX = chartCenter.x + Math.cos(midAngleRad) * (donutRadius + anchorOffset);
                          const anchorY = chartCenter.y + Math.sin(midAngleRad) * (donutRadius + anchorOffset);
                          
                          // Determine box position based on angle with correct mapping
                          let boxPosition, labelX, labelY, textAlign;
                          
                          // Fix the angle mapping to prevent criss-crossing arrows
                          // 0° is at the top, 90° is right, 180° is bottom, 270° is left
                          if (midAngle >= 315 || midAngle < 45) {
                            // Top box (315° to 45°) - arranged horizontally
                            boxPosition = 'top';
                            labelX = chartCenter.x;
                            labelY = chartCenter.y - donutRadius - 60; // Will be adjusted by arrangeHorizontal
                            textAlign = 'center';
                          } else if (midAngle >= 45 && midAngle < 135) {
                            // Right box (45° to 135°) - arranged vertically
                            boxPosition = 'right';
                            labelX = chartCenter.x + donutRadius + 120; // Will be adjusted by arrangeVertical
                            labelY = chartCenter.y;
                            textAlign = 'left';
                          } else if (midAngle >= 135 && midAngle < 225) {
                            // Bottom box (135° to 225°) - arranged horizontally
                            boxPosition = 'bottom';
                            labelX = chartCenter.x;
                            labelY = chartCenter.y + donutRadius + 60; // Will be adjusted by arrangeHorizontal
                            textAlign = 'center';
                          } else {
                            // Left box (225° to 315°) - arranged vertically
                            boxPosition = 'left';
                            labelX = chartCenter.x - donutRadius - 120; // Will be adjusted by arrangeVertical
                            labelY = chartCenter.y;
                            textAlign = 'right';
                          }
                          
                          return {
                            id: getSegmentLabel(segment),
                            segment,
                            labelText: getSegmentLabel(segment),
                            anchorX,
                            anchorY,
                            labelX,
                            labelY,
                            midAngle,
                            midAngleRad,
                            textAlign,
                            boxPosition,
                            height: labelHeight,
                            originalIndex: index
                          };
                        });
                      
                      // Group labels by box position and arrange them appropriately
                      const topLabels = boxLabels.filter(l => l.boxPosition === 'top');
                      const leftLabels = boxLabels.filter(l => l.boxPosition === 'left');
                      const bottomLabels = boxLabels.filter(l => l.boxPosition === 'bottom');
                      const rightLabels = boxLabels.filter(l => l.boxPosition === 'right');

                      // ORDERING: sort groups by their anchor positions to avoid criss-cross
                      // - top/bottom: left → right using anchorX
                      // - left/right: top → bottom using anchorY
                      topLabels.sort((a, b) => a.anchorX - b.anchorX);
                      bottomLabels.sort((a, b) => a.anchorX - b.anchorX);
                      leftLabels.sort((a, b) => a.anchorY - b.anchorY);
                      rightLabels.sort((a, b) => a.anchorY - b.anchorY);
                      
                      // Arrange labels vertically within left and right boxes
                      const arrangeVertical = (labelList, baseX, baseY, spacing = 35) => {
                        if (labelList.length === 0) return;
                        
                        // Calculate total height needed for all labels
                        const totalHeight = (labelList.length - 1) * spacing;
                        const startY = baseY - totalHeight / 2; // Center the group vertically
                        
                        labelList.forEach((label, index) => {
                          label.labelY = startY + (index * spacing);
                        });
                      };
                      
                      // Arrange labels horizontally within top and bottom boxes
                      const arrangeHorizontal = (labelList, baseX, baseY, spacing = 35) => {
                        if (labelList.length === 0) return;
                        
                        // Calculate total width needed for all labels
                        const totalWidth = (labelList.length - 1) * spacing;
                        const startX = baseX - totalWidth / 2; // Center the group horizontally
                        
                        labelList.forEach((label, index) => {
                          label.labelX = startX + (index * spacing);
                        });
                      };
                      
                      // Position boxes with better spacing and alignment
                      // Adjust spacing based on number of labels to prevent overlap
                      const topSpacing = Math.max(120, 600 / Math.max(topLabels.length, 1)); // Horizontal spacing
                      const leftSpacing = Math.max(35, 400 / Math.max(leftLabels.length, 1)); // Vertical spacing
                      const bottomSpacing = Math.max(120, 600 / Math.max(bottomLabels.length, 1)); // Horizontal spacing
                      const rightSpacing = Math.max(35, 400 / Math.max(rightLabels.length, 1)); // Vertical spacing
                      
                      // Arrange top and bottom labels horizontally, left and right labels vertically
                      arrangeHorizontal(topLabels, chartCenter.x, chartCenter.y - donutRadius - 80, topSpacing);
                      arrangeVertical(leftLabels, chartCenter.x - donutRadius - 140, chartCenter.y, leftSpacing);
                      arrangeHorizontal(bottomLabels, chartCenter.x, chartCenter.y + donutRadius + 80, bottomSpacing);
                      arrangeVertical(rightLabels, chartCenter.x + donutRadius + 140, chartCenter.y, rightSpacing);
                      
                      labels = boxLabels;
                    } else {
                      // Radial layout: arrange labels in a circle around the chart
                      labels = detailedSegments
                        .map((segment, index) => {
                          const midAngle = (segment.startAngle + segment.endAngle) / 2;
                          const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                          
                          // Anchor point on the donut outer edge with slight offset to prevent overlapping
                          const anchorOffset = 10; // Small offset to prevent arrow overlap
                          const anchorX = chartCenter.x + Math.cos(midAngleRad) * (donutRadius + anchorOffset);
                          const anchorY = chartCenter.y + Math.sin(midAngleRad) * (donutRadius + anchorOffset);
                          
                          // Check if this label has an override position
                          const currentOverrides = radialLabelPositions;
                          const hasAnyOverrides = Object.keys(currentOverrides).length > 0;
                          const override = currentOverrides[getSegmentLabel(segment)];
                          
                          let labelX, labelY;
                          
                          if (hasAnyOverrides && override) {
                            // Use override position if available
                            labelX = override.x;
                            labelY = override.y;
                          } else {
                            // Initial label position on a circle around the chart (only for non-overridden labels)
                            labelX = chartCenter.x + Math.cos(midAngleRad) * baseRadius;
                            labelY = chartCenter.y + Math.sin(midAngleRad) * baseRadius;
                          }
                          
                          // Determine text alignment based on angle
                          let textAlign = 'left';
                          if (midAngle >= 90 && midAngle <= 270) {
                            textAlign = 'right';
                          }
                          
                          return {
                            id: getSegmentLabel(segment),
                            segment,
                            labelText: getSegmentLabel(segment),
                            anchorX,
                            anchorY,
                            labelX,
                            labelY,
                            midAngle,
                            midAngleRad,
                            textAlign,
                            height: labelHeight,
                            originalIndex: index
                          };
                        })
                        .sort((a, b) => a.midAngle - b.midAngle); // Stable sort by angle
                    }
                    
                    // Enhanced collision resolution with bounding box detection and horizontal spacing
                    const resolveRadialCollisions = (labels, isDragging, hasAnyOverrides) => {
                      if (!d3) return labels;
                      

                      // Fixed margin outside donut chart - increased for absolute separation
                      const margin = 100; // Increased margin to ensure no overlap with chart
                      const minRadius = donutRadius + margin; // Minimum distance from chart center (250px)
                      
                      // Calculate actual bounding box dimensions for each label
                      labels.forEach(label => {
                        // Calculate dynamic width based on text content
                        const nameLength = label.labelText.length;
                        const percentageLength = label.segment.percentage.toFixed(1).length + 1; // +1 for %
                        const estimatedWidth = Math.max(nameLength, percentageLength) * 6 + 20; // 6px per character + reduced padding
                        
                        // Store bounding box dimensions
                        label.bbox = {
                          width: estimatedWidth,
                          height: labelHeight,
                          left: label.labelX - estimatedWidth / 2,
                          right: label.labelX + estimatedWidth / 2,
                          top: label.labelY - labelHeight / 2,
                          bottom: label.labelY + labelHeight / 2
                        };
                      });
                      
                      // NO PRE-SIMULATION CONSTRAINTS - labels can start anywhere
                      
                      // Only run force simulation if no labels have been dragged
                      if (!isDragging && !hasAnyOverrides) {
                        // Create force simulation for collision avoidance
                        const simulation = d3.forceSimulation(labels)
                        // Store simulation reference for drag control
                        simulationRef.current = simulation
                        .force("collision", d3.forceCollide().radius(d => {
                          // Use bounding box dimensions for collision detection
                          const bbox = d.bbox;
                          const radius = Math.max(bbox.width / 2, bbox.height / 2) + padding / 2;
                          return radius;
                        }))
                        .force("chartBoundary", () => {
                          labels.forEach(label => {
                            // Calculate current distance from chart center
                            const dx = label.labelX - chartCenter.x;
                            const dy = label.labelY - chartCenter.y;
                            const currentDistance = Math.sqrt(dx * dx + dy * dy);
                            
                            // If label is too close to chart, push it outward with extra force
                            if (currentDistance < minRadius) {
                              const angle = Math.atan2(dy, dx);
                              // Position label at minimum safe distance + extra buffer
                              const safeRadius = minRadius + 20; // Extra 20px buffer
                              label.labelX = chartCenter.x + Math.cos(angle) * safeRadius;
                              label.labelY = chartCenter.y + Math.sin(angle) * safeRadius;
                              // Update bounding box after position change
                              label.bbox.left = label.labelX - label.bbox.width / 2;
                              label.bbox.right = label.labelX + label.bbox.width / 2;
                              label.bbox.top = label.labelY - label.bbox.height / 2;
                              label.bbox.bottom = label.labelY + label.bbox.height / 2;
                            }
                          });
                        })
                        .force("radialConstraint", () => {
                          labels.forEach(label => {
                            // Calculate current angle and radius
                            const dx = label.labelX - chartCenter.x;
                            const dy = label.labelY - chartCenter.y;
                            const currentRadius = Math.sqrt(dx * dx + dy * dy);
                            const currentAngle = Math.atan2(dy, dx);
                            
                            // Target radius: maintain minimum distance while allowing outward movement
                            const targetRadius = Math.max(minRadius, currentRadius);
                            
                            // Apply constraint to keep labels at or beyond minimum radius
                            if (currentRadius < targetRadius) {
                              label.labelX = chartCenter.x + Math.cos(currentAngle) * targetRadius;
                              label.labelY = chartCenter.y + Math.sin(currentAngle) * targetRadius;
                            }
                            
                            // NO PANEL BOUNDARY CONSTRAINTS - allow labels to move freely during simulation
                            
                            // Update bounding box after position change
                            label.bbox.left = label.labelX - label.bbox.width / 2;
                            label.bbox.right = label.labelX + label.bbox.width / 2;
                            label.bbox.top = label.labelY - label.bbox.height / 2;
                            label.bbox.bottom = label.labelY + label.bbox.height / 2;
                          });
                        })
                        .force("horizontalSpacing", () => {
                          // Add extra horizontal spacing for left/right labels
                          labels.forEach(label => {
                            const angle = Math.atan2(label.labelY - chartCenter.y, label.labelX - chartCenter.x);
                            const isLeftSide = angle >= Math.PI / 2 && angle <= 3 * Math.PI / 2;
                            const isRightSide = angle < Math.PI / 2 || angle > 3 * Math.PI / 2;
                            
                            // Check for horizontal overlaps with other labels
                            labels.forEach(otherLabel => {
                              if (label.id === otherLabel.id) return;
                              
                              const horizontalOverlap = !(label.bbox.right < otherLabel.bbox.left || 
                                                        label.bbox.left > otherLabel.bbox.right);
                              const verticalOverlap = !(label.bbox.bottom < otherLabel.bbox.top || 
                                                      label.bbox.top > otherLabel.bbox.bottom);
                              
                              if (horizontalOverlap && verticalOverlap) {
                                // Resolve horizontal collision by moving labels apart
                                if (isLeftSide) {
                                  label.labelX -= 10; // Move left labels further left
                                  otherLabel.labelX += 10; // Move other labels right
                                } else if (isRightSide) {
                                  label.labelX += 10; // Move right labels further right
                                  otherLabel.labelX -= 10; // Move other labels left
                                }
                                
                                // Update bounding boxes after position changes
                                [label, otherLabel].forEach(l => {
                                  l.bbox.left = l.labelX - l.bbox.width / 2;
                                  l.bbox.right = l.labelX + l.bbox.width / 2;
                                  l.bbox.top = l.labelY - l.bbox.height / 2;
                                  l.bbox.bottom = l.labelY + l.bbox.height / 2;
                                });
                              }
                            });
                          });
                        });
                      
                      // Run simulation for collision resolution
                      for (let i = 0; i < 300; ++i) simulation.tick();
                      
                      // Stop simulation but keep it available for restart
                      simulation.stop();
                      
                      // NO POST-SIMULATION CONSTRAINTS - labels can move freely
                      } // Close the if (!hasDraggedLabels) block
                      
                      // STEP 2: Refinement step - precise bounding box collision resolution
                      const refineBoundingBoxCollisions = (labels) => {
                        const padding = 8; // Small padding for refinement
                        let hasOverlaps = true;
                        let maxRefinementIterations = 20;
                        let iteration = 0;
                        
                        while (hasOverlaps && iteration < maxRefinementIterations) {
                          hasOverlaps = false;
                          iteration++;
                          
                          // Check each label against all others for overlaps
                          for (let i = 0; i < labels.length; i++) {
                            const label = labels[i];
                            
                            for (let j = i + 1; j < labels.length; j++) {
                              const otherLabel = labels[j];
                              
                              // Check for horizontal and vertical overlaps
                              const horizontalOverlap = !(label.bbox.right + padding < otherLabel.bbox.left || 
                                                        label.bbox.left > otherLabel.bbox.right + padding);
                              const verticalOverlap = !(label.bbox.bottom + padding < otherLabel.bbox.top || 
                                                      label.bbox.top > otherLabel.bbox.bottom + padding);
                              
                              if (horizontalOverlap && verticalOverlap) {
                                hasOverlaps = true;
                                
                                // Calculate overlap amounts
                                const overlapX = Math.min(
                                  Math.abs(label.bbox.right - otherLabel.bbox.left),
                                  Math.abs(label.bbox.left - otherLabel.bbox.right)
                                );
                                const overlapY = Math.min(
                                  Math.abs(label.bbox.bottom - otherLabel.bbox.top),
                                  Math.abs(label.bbox.top - otherLabel.bbox.bottom)
                                );
                                
                                // Determine which direction to nudge labels based on their position
                                const labelAngle = Math.atan2(label.labelY - chartCenter.y, label.labelX - chartCenter.x);
                                const otherAngle = Math.atan2(otherLabel.labelY - chartCenter.y, otherLabel.labelX - chartCenter.x);
                                
                                // Nudge labels apart with small movements
                                const nudgeDistance = Math.max(overlapX, overlapY) + padding;
                                
                                // Move label outward along its radial direction
                                const currentRadius = Math.sqrt(
                                  Math.pow(label.labelX - chartCenter.x, 2) + 
                                  Math.pow(label.labelY - chartCenter.y, 2)
                                );
                                const newRadius = currentRadius + nudgeDistance;
                                label.labelX = chartCenter.x + Math.cos(labelAngle) * newRadius;
                                label.labelY = chartCenter.y + Math.sin(labelAngle) * newRadius;
                                
                                // Move other label outward along its radial direction
                                const otherCurrentRadius = Math.sqrt(
                                  Math.pow(otherLabel.labelX - chartCenter.x, 2) + 
                                  Math.pow(otherLabel.labelY - chartCenter.y, 2)
                                );
                                const otherNewRadius = otherCurrentRadius + nudgeDistance;
                                otherLabel.labelX = chartCenter.x + Math.cos(otherAngle) * otherNewRadius;
                                otherLabel.labelY = chartCenter.y + Math.sin(otherAngle) * otherNewRadius;
                                
                                // Update bounding boxes after position changes
                                [label, otherLabel].forEach(l => {
                                  l.bbox.left = l.labelX - l.bbox.width / 2;
                                  l.bbox.right = l.labelX + l.bbox.width / 2;
                                  l.bbox.top = l.labelY - l.bbox.height / 2;
                                  l.bbox.bottom = l.labelY + l.bbox.height / 2;
                                });
                              }
                            }
                          }
                        }
                        
                        return labels;
                      };
                      
                      // Apply refinement step only if no labels have been dragged
                      let refinedLabels;
                      if (!isDragging && !hasAnyOverrides) {
                        refinedLabels = refineBoundingBoxCollisions(labels);
                      } else {
                        refinedLabels = labels; // Use original positions for dragged labels
                      }
                      
                      return refinedLabels;
                    };
                    
                    // Current overrides and drag state
                    const currentOverrides = radialLabelPositions;
                    const isDragging = !!draggingId;
                    const hasAnyOverrides = Object.keys(currentOverrides).length > 0;

                    // Prepare final label list
                    let finalLabels;

                    // AGGRESSIVE BYPASS: If dragging OR any overrides exist, completely skip ALL positioning logic
                    if (isDragging || hasAnyOverrides) {
                      // When dragging or overrides exist, use them directly and skip ANY positioning/collision logic
                      finalLabels = labels.map(l => {
                        const override = currentOverrides[l.id];
                        if (override) {
                          // Apply override position directly without any modification
                          return { ...l, labelX: override.x, labelY: override.y };
                        }
                        // For labels without overrides, keep their current positions
                        return l;
                      });
                      // Proceed to rendering using these overridden positions
                    } else {
                      // No dragging and no overrides → run normal collision resolution
                      finalLabels = resolveRadialCollisions(labels, isDragging, hasAnyOverrides);
                    }

                    // Ensure bbox is properly calculated for all labels right before rendering
                    finalLabels.forEach(label => {
                      // Calculate dynamic width based on text content
                      const nameLength = label.labelText.length;
                      const percentageLength = label.segment.percentage.toFixed(1).length + 1; // +1 for %
                      const estimatedWidth = Math.max(nameLength, percentageLength) * 6 + 20; // 6px per character + reduced padding
                      
                      // Store bounding box dimensions
                      label.bbox = {
                        width: estimatedWidth,
                        height: labelHeight,
                        left: label.labelX - estimatedWidth / 2,
                        right: label.labelX + estimatedWidth / 2,
                        top: label.labelY - labelHeight / 2,
                        bottom: label.labelY + labelHeight / 2
                      };
                    });

                    // Helper: line intersection between two segments
                    const lineIntersection = (x1, y1, x2, y2, x3, y3, x4, y4) => {
                      const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
                      if (denom === 0) return null; // Parallel
                      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
                      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
                      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
                        return { x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1) };
                      }
                      return null;
                    };

                    // Helper: find the NEAREST point on the text box boundary to the anchor
                    const calculateArrowEndPoint = (anchorX, anchorY, labelX, labelY, bbox) => {
                      if (!bbox) return { x: labelX, y: labelY };
                      
                      // Find the closest point on each edge to the anchor
                      let closest = null;
                      let minDist = Infinity;
                      
                      // Top edge
                      const topPoint = findClosestPointOnLine(anchorX, anchorY, bbox.left, bbox.top, bbox.right, bbox.top);
                      if (topPoint) {
                        const dist = Math.hypot(topPoint.x - anchorX, topPoint.y - anchorY);
                        if (dist < minDist) { minDist = dist; closest = topPoint; }
                      }
                      
                      // Right edge
                      const rightPoint = findClosestPointOnLine(anchorX, anchorY, bbox.right, bbox.top, bbox.right, bbox.bottom);
                      if (rightPoint) {
                        const dist = Math.hypot(rightPoint.x - anchorX, rightPoint.y - anchorY);
                        if (dist < minDist) { minDist = dist; closest = rightPoint; }
                      }
                      
                      // Bottom edge
                      const bottomPoint = findClosestPointOnLine(anchorX, anchorY, bbox.left, bbox.bottom, bbox.right, bbox.bottom);
                      if (bottomPoint) {
                        const dist = Math.hypot(bottomPoint.x - anchorX, bottomPoint.y - anchorY);
                        if (dist < minDist) { minDist = dist; closest = bottomPoint; }
                      }
                      
                      // Left edge
                      const leftPoint = findClosestPointOnLine(anchorX, anchorY, bbox.left, bbox.top, bbox.left, bbox.bottom);
                      if (leftPoint) {
                        const dist = Math.hypot(leftPoint.x - anchorX, leftPoint.y - anchorY);
                        if (dist < minDist) { minDist = dist; closest = leftPoint; }
                      }
                      
                      // If we found a closest point, extend it slightly to ensure it touches the box edge
                      if (closest) {
                        // Calculate direction from anchor to closest point
                        const dx = closest.x - anchorX;
                        const dy = closest.y - anchorY;
                        const distance = Math.hypot(dx, dy);
                        
                        if (distance > 0) {
                          // Extend the point slightly beyond the edge to ensure it touches
                          const extension = 4; // 3 pixels extension to ensure contact
                          const normalizedDx = dx / distance;
                          const normalizedDy = dy / distance;
                          
                          closest.x += normalizedDx * extension;
                          closest.y += normalizedDy * extension;
                        }
                      }
                      
                      return closest || { x: labelX, y: labelY };
                    };
                    
                    // Helper: find the closest point on a line segment to a given point
                    const findClosestPointOnLine = (px, py, x1, y1, x2, y2) => {
                      const A = px - x1;
                      const B = py - y1;
                      const C = x2 - x1;
                      const D = y2 - y1;
                      
                      const dot = A * C + B * D;
                      const lenSq = C * C + D * D;
                      
                      if (lenSq === 0) return { x: x1, y: y1 }; // Line segment is a point
                      
                      let param = dot / lenSq;
                      
                      // Clamp to line segment bounds
                      if (param < 0) param = 0;
                      if (param > 1) param = 1;
                      
                      return {
                        x: x1 + param * C,
                        y: y1 + param * D
                      };
                    };
                    
                    // Helper: calculate optimal kink point for professional polyline arrows
                    const calculateOptimalKinkPoint = (startX, startY, endX, endY, bbox, barSegmentAngle) => {
                      if (!bbox) {
                        // Fallback: simple midpoint kink
                        return {
                          x: (startX + endX) / 2,
                          y: (startY + endY) / 2
                        };
                      }

                      // Calculate the direction from start to end
                      const dx = endX - startX;
                      const dy = endY - startY;
                      const distance = Math.hypot(dx, dy);

                      if (distance < 50) {
                        // If very close, no kink needed
                        return {
                          x: (startX + endX) / 2,
                          y: (startY + endY) / 2
                        };
                      }

                      // Flexible-kink arrows only: perpendicular first segment, fixed horizontal second segment
                      const secondSegmentLength = 20;
                      let kinkX;
                      let kinkY;
                      let useDirectConnection = false;

                      if (Math.abs(endX - startX) < 20) {
                        // Text box is directly above/below - use direct connection without kink
                        useDirectConnection = true;
                        kinkX = startX;
                        kinkY = startY;
                      } else {
                        // Text box to left/right - keep a fixed horizontal segment into the label
                        kinkX = endX + (endX > startX ? -secondSegmentLength : secondSegmentLength);
                        kinkY = endY;
                      }

                      // Ensure kink point doesn't interfere with text box
                      const kinkToEndDistance = Math.hypot(endX - kinkX, endY - kinkY);
                      if (!useDirectConnection && kinkToEndDistance < secondSegmentLength) {
                        // Extend the first segment outward if the kink is too close
                        const flexibleAngle = barSegmentAngle - Math.PI / 2; // Perpendicular outward direction
                        const adjustedDistance = Math.max(20, 40 + (secondSegmentLength - kinkToEndDistance));
                        kinkX = startX + Math.cos(flexibleAngle) * adjustedDistance;
                        kinkY = startY + Math.sin(flexibleAngle) * adjustedDistance;
                      }

                      return { x: kinkX, y: kinkY, useDirectConnection };
                    };

                    // Render radial labels with leader lines
                    return finalLabels.map((label) => (
                      <div key={`radial-label-${label.id}`}>
                        {/* Leader line */}
                        <svg
                          className="absolute inset-0 pointer-events-none"
                          width="1000"
                          height="800"
                          style={{ left: '-250px', top: '-250px' }}
                        >
                          {/* Arrow marker definition for label arrows */}
                          <defs>
                            <marker
                              id="labelArrowhead"
                              markerWidth="6"
                              markerHeight="4"
                              refX="5"
                              refY="2"
                              orient="auto"
                            >
                              <polygon
                                points="0 0, 6 2, 0 4"
                                fill="#374151"
                              />
                            </marker>
                          </defs>
                          {(() => {
                            // Use the label's current position directly - it's already in the correct coordinate system
                            // The label positions are calculated relative to the chart container, and SVG is offset by -250px
                            let actualLabelX = label.labelX + 250;
                            let actualLabelY = label.labelY + 250;
                            
                            // Calculate anchor point in the same coordinate system
                            const anchorX = label.anchorX + 250;
                            const anchorY = label.anchorY + 250;
                            
                            // Convert bbox coordinates to SVG coordinate system for proper intersection calculation
                            const svgBbox = label.bbox ? {
                              left: label.bbox.left + 250,
                              right: label.bbox.right + 250,
                              top: label.bbox.top + 250,
                              bottom: label.bbox.bottom + 250
                            } : null;
                            
                            // Debug: Log arrow positioning for this label (moved after svgBbox is defined)
                            console.log(`Arrow for ${label.id}:`, {
                              original: { x: label.labelX, y: label.labelY },
                              actual: { x: actualLabelX, y: actualLabelY },
                              originalBbox: label.bbox,
                              svgBbox: svgBbox,
                              svgOffset: { x: 250, y: 250 }
                            });
                            
                                                        // Find the optimal connection point on the text box edge
                            // This will find the NEAREST point where the arrow can connect to the text box
                            const end = calculateArrowEndPoint(
                              anchorX,        // Start point (chart segment)
                              anchorY,        // Start point (chart segment)
                              actualLabelX,   // Text box center (for fallback only)
                              actualLabelY,   // Text box center (for fallback only)
                              svgBbox         // Text box boundaries for optimal intersection
                            );
                            
                            // Debug: Log the optimal connection point found
                            console.log(`Optimal connection for ${label.id}:`, {
                              anchor: { x: anchorX, y: anchorY },
                              textBoxCenter: { x: actualLabelX, y: actualLabelY },
                              optimalEnd: end,
                              distance: Math.hypot(end.x - anchorX, end.y - anchorY)
                            });
                            
                            // Calculate optimal kink point for professional polyline arrow
                            // We need the BAR SEGMENT angle (not the radius angle) to go perpendicular to the bar
                            const barSegmentAngle = (label.segment.startAngle + label.segment.endAngle) / 2;
                            // Convert to radians: D3 angles are in degrees, SVG uses radians
                            const barSegmentAngleRad = barSegmentAngle * (Math.PI / 180);
                            
                            // Debug: Log the angle calculations
                            console.log(`Label ${label.id}:`, {
                              startAngle: label.segment.startAngle,
                              endAngle: label.segment.endAngle,
                              barSegmentAngle: barSegmentAngle,
                              barSegmentAngleRad: barSegmentAngleRad,
                              barSegmentAngleDegrees: barSegmentAngleRad * 180 / Math.PI
                            });
                            
                            const kinkPoint = calculateOptimalKinkPoint(anchorX, anchorY, end.x, end.y, svgBbox, barSegmentAngleRad);
                            
                            if (kinkPoint.useDirectConnection) {
                              // Direct connection without kink for top/bottom cases
                              return (
                                <line
                                  key={`arrow-${label.id}-${arrowUpdateTrigger}-${actualLabelX}-${actualLabelY}`}
                                  x1={anchorX}
                                  y1={anchorY}
                                  x2={end.x}
                                  y2={end.y}
                                  stroke="#374151"
                                  strokeWidth="1"
                                />
                              );
                            } else {
                              // Normal kinked arrow with two segments
                              return (
                                <g key={`arrow-${label.id}-${arrowUpdateTrigger}-${actualLabelX}-${actualLabelY}`}>
                                  {/* First segment: from anchor to kink */}
                                  <line
                                    x1={anchorX}
                                    y1={anchorY}
                                    x2={kinkPoint.x}
                                    y2={kinkPoint.y}
                                    stroke="#374151"
                                    strokeWidth="1"
                                  />
                                  {/* Second segment: from kink to text box */}
                                  <line
                                    x1={kinkPoint.x}
                                    y1={kinkPoint.y}
                                    x2={end.x}
                                    y2={end.y}
                                    stroke="#374151"
                                    strokeWidth="1"
                                  />
                                </g>
                              );
                            }
                          })()}
            </svg>
            
                        {/* Label */}
                        <div
                          data-label-id={label.id}
                          onMouseDown={(e) => {
                            // Enable drag only in detailed view
                            if (!detailedView) return;
                            const rect = chartPanelRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            // Record drag start and offset so cursor stays relative inside the box
                            setDraggingId(label.id);
                            const mouseX = e.clientX - rect.left;
                            const mouseY = e.clientY - rect.top;
                            setDragOffset({ x: mouseX - label.labelX, y: mouseY - label.labelY });
                          }}
                          className="absolute cursor-move bg-white border border-gray-200 rounded px-2 py-1 shadow-sm shadow-lg select-none"
                          style={{
                            left: `${label.labelX}px`,
                            top: `${label.labelY}px`,
                            transform: 'translate(-50%, -50%)',
                            textAlign: label.textAlign,
                            zIndex: 1000,
                            whiteSpace: 'nowrap',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span className="text-xs font-medium text-gray-700" style={{ fontSize: '10px' }}>
                              {label.labelText}
                              {!shouldShowPercentages && (
                                <span className="text-xs font-semibold text-emerald-600 ml-1" style={{ fontSize: '10px' }}>
                                  {roundOffPercentages ? Math.round(label.segment.percentage) : label.segment.percentage.toFixed(1)}%
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                
                {/* Tooltip anchored to bottom of left panel without moving the chart */}
                <div className={`absolute left-3 right-3 bottom-12 sm:bottom-14 bg-gray-800/95 text-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg text-center ${tooltipHeight} flex items-center justify-center text-[11px] sm:text-xs leading-snug shadow-md`}>
                  {selectedSegment ? (
                    <div className="w-full">
                      <div className="font-semibold text-emerald-400 text-[11px] sm:text-xs mb-1">
                        {selectedSegment.type === 'category' ? selectedSegment.category : selectedSegment.name}
                      </div>
                      <div className="space-y-0.5">
                        <div>
                          {formatValue(selectedSegment.value)}
                          {formatPerM2Yr(selectedSegment.value)
                            ? ` • ${formatPerM2Yr(selectedSegment.value)}`
                            : ''}
                        </div>
                        <div>{roundOffPercentages ? Math.round(selectedSegment.percentage) : selectedSegment.percentage.toFixed(1)}% of total</div>
                        {selectedSegment.type === 'component' && (
                          <div className="text-gray-300">Category: {selectedSegment.category}</div>
                        )}
                        {selectedSegment.type === 'category' && (
                          <div className="text-gray-300">Category Group</div>
                        )}
                </div>
              </div>
                  ) : (
                    <div className="text-white">
                      Click any bar in the chart to view the elements details
              </div>
            )}
                </div>
              </div>
              
              {/* Right side - Legend with scrollable content */}
              <div className="w-full lg:w-2/5 p-4 sm:p-6 border-t border-gray-200 lg:border-t-0 lg:border-l flex flex-col overflow-y-auto mt-6 lg:mt-0 max-h-[60vh] lg:max-h-[80vh]">
                {/* Scrollable legend content */}
                <div className="relative -top-8 flex-1 pr-0 sm:pr-2 space-y-6">
                  {rightPanelOverride ? (
                    <div className="space-y-4">
                      {rightPanelOverride}
                    </div>
                  ) : null}
                  {/* Categories Legend */}
                  {!hideDefaultLegends && !hideCategoryLegend && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 sticky top-0 bg-white pb-2 z-10">Categories (Inner Ring)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-4">
                      {popupChartData.categoryData.map((segment) => (
                        <div 
                          key={`category-${segment.category}`}
                                                   className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-20 ${
                           selectedSegment?.category === segment.category && selectedSegment?.type === 'category' ? 'bg-gray-100 scale-105' : 'hover:bg-gray-50'
                         }`}
                         style={{
                           transition: 'all 75ms ease-out'
                         }}
                         onClick={() => handleSegmentClick(segment)}
                        >
                          <div 
                            className="w-5 h-5 rounded-full"
                            style={{ backgroundColor: segment.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800">{segment.category}</div>
                            <div className="text-xs text-gray-500">
                              {Math.round(segment.percentage)}% • {formatValue(segment.value)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
          </div>
                  )}
          
                  {/* Components Legend */}
                  {!hideDefaultLegends && !hideComponentLegend && (
                  <div>
                    <div className="flex items-center justify-between mb-3 sticky top-0 bg-white pb-2 z-10">
                      <h3 className="text-lg font-semibold text-gray-800">Components (Outer Ring)</h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setComponentSortOrder('highToLow')}
                          className={`px-2 py-1 text-xs rounded ${
                            componentSortOrder === 'highToLow' 
                              ? 'bg-emerald-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          High→Low
                        </button>
                        <button
                          onClick={() => setComponentSortOrder('lowToHigh')}
                          className={`px-2 py-1 text-xs rounded ${
                            componentSortOrder === 'lowToHigh' 
                              ? 'bg-emerald-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Low→High
                        </button>
                        <button
                          onClick={() => setComponentSortOrder('byCategory')}
                          className={`px-2 py-1 text-xs rounded ${
                            componentSortOrder === 'byCategory' 
                              ? 'bg-emerald-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          By Category
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 pb-4">
                      {[...popupChartData.componentData]
                        .sort((a, b) => {
                          if (componentSortOrder === 'highToLow') {
                            return b.value - a.value;
                          } else if (componentSortOrder === 'lowToHigh') {
                            return a.value - b.value;
                                                      } else if (componentSortOrder === 'byCategory') {
                              // First sort by category in the same order as pie chart, then by value within each category
                              const categoryOrder = ['Structure', 'Envelope', 'Finishes', 'MEP', 'Others'];
                              const aIndex = categoryOrder.indexOf(a.category);
                              const bIndex = categoryOrder.indexOf(b.category);
                              
                              if (aIndex !== bIndex) {
                                return aIndex - bIndex; // Sort by category order
                              } else {
                                return b.value - a.value; // High to low within same category
                              }
                          }
                          return 0;
                        })
                        .map((segment) => (
                        <div 
                          key={`component-${segment.name}`}
                                                   className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-20 ${
                           selectedSegment?.name === segment.name && selectedSegment?.type === 'component' ? 'bg-gray-100 scale-105' : 'hover:bg-gray-50'
                         }`}
                         style={{
                           transition: 'all 75ms ease-out'
                         }}
                         onClick={() => handleSegmentClick(segment)}
              >
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">{segment.name}</div>
                  <div className="text-xs text-gray-500">
                              {roundOffPercentages ? Math.round(segment.percentage) : segment.percentage.toFixed(1)}% • {formatValue(segment.value)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InteractivePieChart;
